import React, { useEffect, useMemo } from 'react';
import { format, startOfDay, endOfDay, eachDayOfInterval } from 'date-fns';
import { useTheme, Box } from '@mui/material';


const Timeline = React.forwardRef((props, ref) => {
    const {
        cellWidth = 20,
        rowHeight = 60,
        from = new Date(),
        to = new Date(),
    } = props;
    
    const theme = useTheme();

    const hourStep = useMemo(() => {
        if (cellWidth < 4) return 12;
        if (cellWidth < 8) return 4;
        if (cellWidth <= 12) return 2;
        return 1;
    }, [cellWidth]);

    const dates = useMemo(() => {
        if (!from || !to) return [];
        try {
            const interval = eachDayOfInterval({
                start: startOfDay(new Date(from)),
                end: endOfDay(new Date(to)),
            });
            const dateFmt = cellWidth < 4 ? 'dd' : 'dd MMM';
            return interval.map(date => ({
                date,
                label: format(date, dateFmt),
            }));
        } catch {
            return [];
        }
    }, [from, to, cellWidth]);

    const getDayColor = (date, theme) => {
        const day = new Date(date).getDay(); // 0 = Sun, 6 = Sat
        if (day === 6) return theme.palette.info.light;   // Sat
        if (day === 0) return theme.palette.error.light;  // Sun
        return 'default';
    };

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const onWheel = (e) => {
            // chỉ convert scroll dọc → ngang
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                el.scrollLeft += e.deltaY;
            }
        };

        el.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            el.removeEventListener('wheel', onWheel);
        };
    }, [ref]);


    return (
        <Box 
            ref={ref}
            sx={{ 
                width: '100%', 
                height: rowHeight,          
                display: 'flex',               
                overflowX: 'hidden',
                overflowY: 'scroll', 
                bgcolor: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.neutral.deep}`, 
                '&::-webkit-scrollbar-track': { 
                    backgroundColor: 'background.default',
                },
            }}
        >
            {dates?.map((d, index) => (
                <Box 
                    key={index}
                    sx={{ flexShrink: 0 }}
                >
                    {/* Hàng hiển thị Ngày */}
                    <Box 
                        sx={{ 
                            minWidth: 24 * cellWidth, 
                            height: rowHeight / 2 - 0.5, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontSize: '0.8rem', 
                            color: getDayColor(d.date, theme),
                            borderLeft: index === 0 && `1px solid ${theme.palette.divider}`,
                            borderRight: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        {d.label}
                    </Box>

                    {/* Hàng hiển thị Giờ (0-23) */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            height: rowHeight / 2, 
                            borderTop: `1px solid ${theme.palette.divider}`, 
                            borderLeft: index === 0 && `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        {Array.from({ length: 24 / hourStep }).map((_, i) => {
                            const h = i * hourStep;
                            const currentBoxWidth = cellWidth * hourStep;
                            
                            return (
                                <Box 
                                    key={h} 
                                    sx={{ 
                                        width: currentBoxWidth, 
                                        minWidth: currentBoxWidth, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        fontSize: '0.65rem',
                                        color: theme.palette.text.secondary,
                                        borderRight: `1px solid ${theme.palette.divider}`, 
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    {/* Chỉ hiện số nếu chiều rộng ô > 12px để tránh chồng lấp */}
                                    {currentBoxWidth > 12 ? h : ''}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            ))}
        </Box>
    );
});

export default React.memo(Timeline);


/* --- CANVAS --- */
// import React, { useEffect, useMemo, useRef } from 'react';
// import { format, eachDayOfInterval, startOfDay, endOfDay } from 'date-fns';
// import { useTheme, Box } from '@mui/material';

// const Timeline = React.forwardRef((props, ref) => {
//     const {
//         syncX,
//         topRightScrollRef,
//         bottomRightScrollRef,
//         cellWidth = 20,
//         rowHeight = 60,
//         from = new Date(),
//         to = new Date(),
//     } = props;

//     const theme = useTheme();
//     const canvasRef = useRef(null);
    
//     // 2. Logic bước nhảy giờ theo yêu cầu của bạn
//     const hourStep = useMemo(() => {
//         if (cellWidth < 4) return 12; // Zoom cực nhỏ: hiện mỗi 12 tiếng
//         if (cellWidth < 8) return 4;  // Zoom nhỏ: hiện mỗi 4 tiếng
//         if (cellWidth <= 12) return 2; // Zoom vừa: hiện mỗi 2 tiếng
//         return 1; // Mặc định
//     }, [cellWidth]);
    
//     const dates = useMemo(() => {
//         try {
//             const start = startOfDay(new Date(from));
//             const end = endOfDay(new Date(to));
//             const interval = eachDayOfInterval({ start, end });

//             return interval.map(date => ({
//                 date: date,
//                 label: format(date, 'dd MMM'),
//             }));
//         } catch (error) {
//             return [{
//                 date: new Date(),
//                 label: format(new Date(), 'dd MMM'),
//             }];
//         }
//     }, [from, to]);

//     useEffect(() => {
//         if (dates.length === 0) return;

//         const canvas = canvasRef.current;
//         if (!canvas) return;

//         const ctx = canvas.getContext('2d');
//         const dpr = window.devicePixelRatio || 1;
        
//         const totalWidth = dates.length * 24 * cellWidth;
//         const totalHeight = rowHeight;

//         canvas.width = totalWidth * dpr;
//         canvas.height = totalHeight * dpr;
//         canvas.style.width = `${totalWidth}px`;
//         canvas.style.height = `${totalHeight}px`;
        
//         ctx.save();
//         ctx.scale(dpr, dpr);

//         const dividerColor = theme.palette.divider;
//         const textColorSecondary = theme.palette.text.secondary;
//         const bgColor = theme.palette.background.default;

//         // Vẽ nền trắng/tối cho toàn bộ thanh timeline
//         ctx.fillStyle = bgColor;
//         ctx.fillRect(0, 0, totalWidth, totalHeight);

//         // --- VẼ BORDER LEFT TẠI VỊ TRÍ BẮT ĐẦU (Index 0) ---
//         ctx.strokeStyle = dividerColor;
//         ctx.lineWidth = 1;
//         ctx.beginPath();
//         ctx.moveTo(0, 0);
//         ctx.lineTo(0, totalHeight);
//         ctx.stroke();

//         dates.forEach((d, index) => {
//             const dateX = index * 24 * cellWidth;
//             const dayHeight = rowHeight / 2;

//             // --- VẼ ĐƯỜNG CHIA NGÀY ---
//             ctx.strokeStyle = dividerColor;
//             ctx.lineWidth = 1;
//             ctx.beginPath();
//             ctx.moveTo(dateX + 24 * cellWidth + 0.5, 0);
//             ctx.lineTo(dateX + 24 * cellWidth + 0.5, totalHeight);
//             ctx.stroke();

//             // Hiển thị nhãn ngày nếu vùng chứa ngày > 30px
//             if (cellWidth * 24 > 30) {
//                 const getDayColor = (date) => {
//                     const day = date.getDay();
//                     if (day === 6) return theme.palette.info.main;
//                     if (day === 0) return theme.palette.error.main;
//                     return theme.palette.text.primary;
//                 };

//                 ctx.fillStyle = getDayColor(d.date);
//                 ctx.font = `${Math.max(0.6, Math.min(0.8, cellWidth / 10))}rem Inter, Arial`;
//                 ctx.textAlign = 'center';
//                 ctx.textBaseline = 'middle';
//                 ctx.fillText(d.label, dateX + (12 * cellWidth), dayHeight / 2);
//             }

//             // --- VẼ HÀNG GIỜ ---
//             // Đường kẻ ngang phân cách Ngày và Giờ
//             ctx.beginPath();
//             ctx.moveTo(dateX + 0.5, dayHeight);
//             ctx.lineTo(dateX + 24 * cellWidth+ 0.5, dayHeight);
//             ctx.stroke();

//             ctx.font = '0.6rem Inter, Arial';
//             ctx.fillStyle = textColorSecondary;

//             for (let h = 0; h < 24; h += hourStep) {
//                 const hourX = dateX + (h * cellWidth);
                
//                 // Vẽ vạch dọc phụ cho từng bước giờ (hourStep)
//                 if (h > 0) {
//                     ctx.beginPath();
//                     ctx.moveTo(hourX + 0.5, dayHeight);
//                     ctx.lineTo(hourX + 0.5, totalHeight);
//                     ctx.stroke();
//                 }

//                 // Hiển thị số giờ (Căn giữa khoảng giữa 2 vạch)
//                 // Chỉ hiện nếu khoảng cách giữa 2 vạch > 12px để tránh đè chữ
//                 if (cellWidth * hourStep > 12) {
//                     ctx.textAlign = 'center';
//                     const textCenterPos = hourX + (cellWidth * hourStep / 2);
//                     ctx.fillText(h.toString(), textCenterPos, dayHeight + (dayHeight / 2));
//                 }
//             }
//         });

//         // Vẽ đường biên dưới cùng
//         ctx.strokeStyle = theme.palette.neutral.deep;
//         ctx.beginPath();
//         ctx.moveTo(0, totalHeight);
//         ctx.lineTo(totalWidth, totalHeight);
//         ctx.stroke();
        
//         ctx.restore();
//     }, [theme, cellWidth, rowHeight, dates, hourStep]);


//     return (
//         <Box 
//             ref={ref} 
//             sx={{ 
//                 width: '100%', 
//                 height: rowHeight,          
//                 overflowX: 'hidden',
//                 overflowY: 'scroll', 
//                 '&::-webkit-scrollbar-track': { 
//                     backgroundColor: 'background.default',
//                 },
//             }}
//         >
//             <canvas
//                 ref={canvasRef}
//                 style={{
//                     display: 'block',
//                     pointerEvents: 'none'
//                 }}
//             />
//         </Box>
//     );
// });

// export default React.memo(Timeline);